import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useUser } from "./Context/UserContext";
import MindteckLogo from "../assets/Mindteck Logo.png";
import "../styles/SsoLogin.css";

/* ────────────────────────────────────────────────────────────────────────────
   SSO landing page — /sso?token=...

   This is the door the central portal pushes employees through. There is no
   form here on purpose: the token in the URL *is* the credential, so the page's
   whole job is to hand it to the server, store the session it gets back, and
   get out of the way.

   Sequence:
     1. Read ?token= from the URL.
     2. Strip it from the address bar immediately, before any network call, so
        it never sits in the visible URL, gets bookmarked, or leaks through a
        Referer header on the next navigation.
     3. POST it to /api/sso/login.
     4. On success store the session exactly the way Login.js does for an
        internal user, then replace this entry in the history stack with the
        dashboard — pressing Back must not land on a spent token.
   ──────────────────────────────────────────────────────────────────────────── */

function SsoLogin() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { loginUser } = useUser();

    const [error, setError] = useState("");
    const [status, setStatus] = useState("Verifying your sign-in…");

    // React can mount an effect twice (StrictMode, fast refresh, a stray
    // re-render). The token is single-use server-side, so a second POST would
    // be rejected as a replay and show a false error. Run exactly once.
    const started = useRef(false);

    const signIn = useCallback(async (token) => {
        try {
            const { data } = await axios.post("/api/sso/login", { token });

            const { empId, userTypeIds, roles, menus } = data;

            // Same normalisation Login.js applies — the API returns camelCase
            // from the JSON serialiser, but the rest of the app reads PascalCase.
            const mappedMenus = (menus || []).map(menu => ({
                Id: menu.id || menu.Id,
                MenuName: menu.menuName || menu.MenuName,
                MenuLink: menu.menuLink || menu.MenuLink,
                Parentmenuid: menu.parentmenuid || menu.Parentmenuid || null,
            }));

            if (!empId || !mappedMenus.length) {
                setError("Your account is missing role details. Contact your VMS administrator.");
                setStatus("");
                return;
            }

            setStatus("Signed in. Opening your dashboard…");

            loginUser({
                empId,
                userTypeIds,
                roles,
                isVendor: false,
                menus: mappedMenus,
            });

            // replace, not push: the /sso URL must not be reachable via Back.
            navigate("/master-page", {
                replace: true,
                state: { justLoggedIn: true },
            });
        } catch (err) {
            setStatus("");
            setError(
                err.response?.data?.message ||
                "We could not sign you in from the portal. Open VMS again from the portal, or sign in below."
            );
        }
    }, [loginUser, navigate]);

    useEffect(() => {
        if (started.current) return;
        started.current = true;

        const token = searchParams.get("token");

        if (!token) {
            setStatus("");
            setError("This sign-in link is incomplete. Open VMS again from the portal.");
            return;
        }

        // Scrub the token out of the address bar before doing anything else.
        window.history.replaceState({}, document.title, "/sso");

        signIn(token);
    }, [searchParams, signIn]);

    return (
        <div className="sso-page">
            <div className="sso-card" role="status" aria-live="polite">
                <img src={MindteckLogo} alt="Mindteck" className="sso-logo" />

                {error ? (
                    <>
                        <div className="sso-icon sso-icon-error" aria-hidden="true">!</div>
                        <h1 className="sso-title">Sign-in could not be completed</h1>
                        <p className="sso-body">{error}</p>
                        <Link to="/login" className="vms-btn vms-btn-primary sso-action">
                            Sign in with your password
                        </Link>
                    </>
                ) : (
                    <>
                        <div className="sso-spinner" aria-hidden="true" />
                        <h1 className="sso-title">Signing you in</h1>
                        <p className="sso-body">{status}</p>
                    </>
                )}
            </div>
        </div>
    );
}

export default SsoLogin;
